import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { memberCopy, type MemberCopy } from "../i18n/member";
import "../styles/member.css";

export type MemberRecord = Record<string, unknown>;
export type MemberRequest = (endpoint: string, method: "GET" | "POST" | "PATCH" | "DELETE", payload?: MemberRecord, idempotencyKey?: string) => Promise<unknown>;
export type MemberUpload = (files: File[], options?: { visibility?: "public" | "private"; purpose?: "verification" }) => Promise<MemberRecord[]>;
export type MemberViewProps = {
  locale: string;
  ui: Record<string, string>;
  userId: string;
  request: MemberRequest;
  upload: MemberUpload;
  navigate: (href: string) => void;
};

type Notice = { error: boolean; text: string } | null;
type MemberContentKind = "articles" | "clips" | "documents";
type MemberContentMode = "list" | "detail" | "create" | "drafts";

function isRecord(value: unknown): value is MemberRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function memberError(error: unknown, copy: MemberCopy): string {
  const data = isRecord(error) && isRecord(error.data) ? error.data : error;
  const code = isRecord(data) && typeof data.code === "string" ? data.code : "";
  if (Object.hasOwn(copy, code)) return copy[code as keyof MemberCopy];
  if (error instanceof Error && error.message && !/^[a-z0-9_]+$/u.test(error.message) && !/^(Failed to fetch|NetworkError|HTTP \d)/u.test(error.message)) return error.message;
  return copy.request_failed;
}

function rowText(row: MemberRecord, key: string): string {
  return typeof row[key] === "string" ? row[key] : "";
}

function resultRows(payload: unknown, copy: MemberCopy): MemberRecord[] {
  if (!isRecord(payload)) throw new Error(copy.request_failed);
  if (Array.isArray(payload.data)) return payload.data.filter(isRecord);
  if (isRecord(payload.data)) return [payload.data];
  throw new Error(copy.request_failed);
}

export function memberStatusLabel(status: unknown, copy: MemberCopy, ui: Record<string, string>): string {
  const key = `status_${String(status)}`;
  return Object.hasOwn(copy, key) ? copy[key as keyof MemberCopy] : ui.status;
}

function dateText(value: unknown, locale: string): string {
  if (typeof value !== "string" || !value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function money(value: unknown, currency: unknown, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: String(currency || "EUR") }).format(Number(value) / 100);
}

function MemberNotice({ notice }: { notice: Notice }) {
  return notice ? <p className={`member-notice${notice.error ? " member-notice-error" : ""}`} role={notice.error ? "alert" : "status"}>{notice.text}</p> : null;
}

function MemberLink({ href, navigate, children, className = "base-button outlined" }: { href: string; navigate: MemberViewProps["navigate"]; children: ReactNode; className?: string }) {
  return <a className={className} href={href} onClick={(event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(href);
  }}>{children}</a>;
}

export function useMemberPolling(refresh: () => void, enabled: boolean, intervalMs = 5000): void {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (!enabled) return;
    const update = () => {
      if (document.visibilityState === "visible") refreshRef.current();
    };
    const timer = window.setInterval(update, intervalMs);
    document.addEventListener("visibilitychange", update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", update);
    };
  }, [enabled, intervalMs]);
}

function useMemberResource(endpoint: string | null, request: MemberRequest, copy: MemberCopy) {
  const [revision, setRevision] = useState(0);
  const [page, setPage] = useState<{ endpoint: string | null; cursor: string | null }>({ endpoint, cursor: null });
  const [state, setState] = useState<{ endpoint: string | null; rows: MemberRecord[]; nextCursor: string | null; loading: boolean; error: string }>({ endpoint, rows: [], nextCursor: null, loading: Boolean(endpoint), error: "" });
  const cursor = page.endpoint === endpoint ? page.cursor : null;
  const requestRef = useRef(request);
  const activeRef = useRef<symbol | null>(null);
  const refreshQueuedRef = useRef(false);
  const copyRef = useRef(copy);
  requestRef.current = request;
  copyRef.current = copy;
  useEffect(() => {
    let active = true;
    if (!endpoint) {
      setState({ endpoint, rows: [], nextCursor: null, loading: false, error: "" });
      return;
    }
    setState((current) => ({ ...current, endpoint, rows: current.endpoint === endpoint ? current.rows : [], loading: true, error: "" }));
    const activeRequest = Symbol();
    activeRef.current = activeRequest;
    const url = cursor ? `${endpoint}${endpoint.includes("?") ? "&" : "?"}cursor=${encodeURIComponent(cursor)}` : endpoint;
    requestRef.current(url, "GET").then((payload) => {
      if (!active) return;
      const rows = resultRows(payload, copyRef.current);
      const nextCursor = isRecord(payload) && typeof (payload.nextCursor ?? payload.next_cursor) === "string" ? String(payload.nextCursor ?? payload.next_cursor) : null;
      setState((current) => {
        const existing = cursor && current.endpoint === endpoint ? current.rows : [];
        const ids = new Set(existing.map((row) => String(row.id)));
        return { endpoint, rows: [...existing, ...rows.filter((row) => !ids.has(String(row.id)))], nextCursor, loading: false, error: "" };
      });
    }).catch((error: unknown) => {
      if (active) setState((current) => ({ ...current, loading: false, error: memberError(error, copyRef.current) }));
    }).finally(() => {
      if (activeRef.current === activeRequest) {
        activeRef.current = null;
        if (active && refreshQueuedRef.current) {
          refreshQueuedRef.current = false;
          setPage({ endpoint, cursor: null });
          setRevision((value) => value + 1);
        }
      }
    });
    return () => { active = false; };
  }, [endpoint, cursor, revision]);
  const reload = useCallback(() => {
    if (activeRef.current) { refreshQueuedRef.current = true; return; }
    setPage({ endpoint, cursor: null });
    setRevision((value) => value + 1);
  }, [endpoint]);
  return {
    ...state,
    rows: state.endpoint === endpoint ? state.rows : [],
    reload,
    loadMore: () => { if (state.nextCursor && !state.loading && !activeRef.current) setPage({ endpoint, cursor: state.nextCursor }); }
  };
}

function MemberResourceState({ resource, ui, empty, children }: { resource: ReturnType<typeof useMemberResource>; ui: Record<string, string>; empty: string; children: ReactNode }) {
  return <>
    {resource.loading && resource.rows.length === 0 && <p role="status">{ui.loadingResults}</p>}
    {resource.error && <div className="member-notice member-notice-error" role="alert"><p>{resource.error}</p><button className="base-button outlined" type="button" onClick={resource.reload}>{ui.retry}</button></div>}
    {!resource.loading && !resource.error && resource.rows.length === 0 && <p className="member-empty">{empty}</p>}
    {children}
    {resource.nextCursor && <button className="base-button outlined" type="button" disabled={resource.loading} onClick={resource.loadMore}>{resource.loading ? ui.loadingResults : ui.loadMore}</button>}
  </>;
}

function contentHref(kind: MemberContentKind, row: MemberRecord): string {
  return `/${kind}/${encodeURIComponent(String(kind === "articles" ? row.slug || row.id : row.id))}`;
}

function fileHref(row: MemberRecord): string {
  return rowText(row, "file_url") || rowText(row, "public_url");
}

function ContentVideo({ row, ui, copy }: { row: MemberRecord; ui: Record<string, string>; copy: MemberCopy }) {
  const href = fileHref(row);
  return href ? <video className="member-video" controls playsInline preload="metadata" aria-label={rowText(row, "title") || ui.movie}>
    <source src={href} type={rowText(row, "mime_type")} />
    <p>{copy.transcriptUnavailable} <a href={href} download>{copy.downloadFile}</a></p>
  </video> : null;
}

function DeleteContentAction({ row, kind, props, onDeleted }: { row: MemberRecord; kind: MemberContentKind; props: MemberViewProps; onDeleted: () => void }) {
  const copy = memberCopy(props.locale);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  async function remove() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await props.request(`/api/${kind}/${encodeURIComponent(String(row.id))}`, "DELETE");
      setConfirming(false);
      onDeleted();
    } catch (error) {
      setNotice({ error: true, text: memberError(error, copy) });
    } finally { setBusy(false); }
  }
  return <div className="member-delete-action">
    {!confirming ? <button className="base-button transparent" type="button" onClick={() => setConfirming(true)}>{copy.deleteContent}</button> : <div className="member-confirm" role="group" aria-label={copy.deleteContent}>
      <p>{copy.deleteContentConfirm}</p>
      <div className="form-actions"><button className="base-button danger" type="button" disabled={busy} onClick={() => void remove()}>{busy ? props.ui.submitting : props.ui.deleteItem}</button><button className="base-button outlined" type="button" disabled={busy} onClick={() => setConfirming(false)}>{props.ui.cancel}</button></div>
    </div>}
    <MemberNotice notice={notice} />
  </div>;
}

export function MemberContentView(props: MemberViewProps & { kind: MemberContentKind; mode?: MemberContentMode; identifier?: string }) {
  const { kind, mode = "list", identifier, ui } = props;
  const copy = memberCopy(props.locale);
  const endpoint = mode === "create" ? null : mode === "detail" ? `/api/${kind}/${encodeURIComponent(identifier || "")}` : `/api/${kind}${mode === "drafts" ? "?mine=1&status=draft" : ""}`;
  const resource = useMemberResource(endpoint, props.request, copy);
  if (mode === "create") return <MemberContentCreate {...props} />;
  return <section className={`contract-page member-content-page member-content-${kind}`}>
    <header className="member-heading"><h1>{mode === "drafts" ? copy.drafts : ui[kind]}</h1><nav className="member-actions" aria-label={ui.quickActions}>
      {kind === "articles" && <><MemberLink href="/articles/editor" navigate={props.navigate}>{copy.createArticle}</MemberLink><MemberLink href={mode === "drafts" ? "/articles" : "/articles?mine=1&status=draft"} navigate={props.navigate}>{mode === "drafts" ? copy.allArticles : copy.drafts}</MemberLink></>}
      {kind !== "articles" && <MemberLink href={`/${kind}/create`} navigate={props.navigate}>{kind === "clips" ? copy.uploadClip : copy.uploadDocument}</MemberLink>}
    </nav></header>
    <MemberResourceState resource={resource} ui={ui} empty={mode === "drafts" ? copy.noDrafts : copy.noContent}>
      <div className="member-content-list">{resource.rows.map((row) => {
        const owner = String(row.user_id ?? row.author_id) === props.userId;
        const href = contentHref(kind, row);
        const title = rowText(row, "title") || (kind === "clips" ? ui.movie : ui[kind]);
        return <article className="member-content-card" key={String(row.id)}>
          <header><h2>{mode === "detail" ? title : <MemberLink className="profile-name-link" href={href} navigate={props.navigate}>{title}</MemberLink>}</h2>
            <div className="member-meta"><time dateTime={rowText(row, "created_at")}>{dateText(row.created_at, props.locale)}</time>{Boolean(row.status) && <span>{memberStatusLabel(row.status, copy, ui)}</span>}</div>
          </header>
          {kind === "clips" && <ContentVideo row={row} ui={ui} copy={copy} />}
          {Boolean(row.body || row.description) && <div className={mode === "detail" ? "member-long-form" : "member-content-summary"}>{String(row.body || row.description)}</div>}
          <footer className="member-actions">
            {kind === "articles" && mode !== "detail" && <MemberLink href={href} navigate={props.navigate}>{copy.readArticle}</MemberLink>}
            {kind === "articles" && owner && <MemberLink href={`/articles/editor?id=${encodeURIComponent(String(row.id))}`} navigate={props.navigate}>{copy.editArticle}</MemberLink>}
            {kind === "documents" && fileHref(row) && <a className="base-button outlined" href={fileHref(row)} download={rowText(row, "original_name") || true}>{copy.downloadDocument}</a>}
          </footer>
          {owner && <DeleteContentAction row={row} kind={kind} props={props} onDeleted={() => mode === "detail" ? props.navigate(`/${kind}`) : resource.reload()} />}
        </article>;
      })}</div>
    </MemberResourceState>
  </section>;
}

function MemberContentCreate(props: MemberViewProps & { kind: MemberContentKind }) {
  const { kind, ui } = props;
  const copy = memberCopy(props.locale);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const uploadRef = useRef<{ file: File; id: string } | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!file) { setNotice({ error: true, text: copy.fileRequired }); return; }
    if (kind === "clips" && !file.type.startsWith("video/")) { setNotice({ error: true, text: copy.videoRequired }); return; }
    const form = new FormData(event.currentTarget);
    const visibility = String(form.get("visibility"));
    setBusy(true);
    setNotice(null);
    try {
      let fileId = uploadRef.current?.file === file ? uploadRef.current.id : "";
      if (!fileId) {
        const uploaded = await props.upload([file], { visibility: visibility === "public" ? "public" : "private" });
        fileId = String(uploaded[0]?.id || "");
        if (!fileId) throw new Error(copy.request_failed);
        uploadRef.current = { file, id: fileId };
      }
      const payload = kind === "clips" ? { fileId, body: String(form.get("body") || ""), visibility, language: props.locale } : { fileId, title: String(form.get("title")), description: String(form.get("description") || ""), documentType: String(form.get("documentType")), visibility };
      const result = await props.request(`/api/${kind}`, "POST", payload);
      if (!isRecord(result) || !result.id) throw new Error(copy.request_failed);
      props.navigate(contentHref(kind, result));
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); }
    finally { setBusy(false); }
  }
  return <section className="contract-page member-content-page"><h1>{kind === "clips" ? copy.uploadClip : copy.uploadDocument}</h1><form className="settings-form member-form" onSubmit={submit}>
    <label><span>{kind === "clips" ? copy.selectVideo : copy.selectDocument}</span><input type="file" required accept={kind === "clips" ? "video/*" : undefined} disabled={busy} onChange={(event) => { setFile(event.currentTarget.files?.[0] || null); uploadRef.current = null; setNotice(null); }} /></label>
    {kind === "clips" ? <label><span>{ui.caption}</span><textarea name="body" maxLength={5000} /></label> : <>
      <label><span>{ui.title}</span><input name="title" required minLength={2} maxLength={160} /></label>
      <label><span>{ui.description}</span><textarea name="description" maxLength={5000} /></label>
      <label><span>{ui.documentType}</span><select name="documentType"><option value="identity">{ui.identity}</option><option value="work">{ui.workDocument}</option><option value="company">{ui.company}</option></select></label>
    </>}
    <label><span>{ui.visibility}</span><select name="visibility" defaultValue={kind === "documents" ? "private" : "public"}><option value="public">{ui.public}</option><option value="friends">{ui.friendsOnly}</option><option value="private">{ui.onlyMe}</option></select></label>
    <div className="form-actions"><button className="base-button primary" type="submit" disabled={busy}>{busy ? ui.uploadingMedia : kind === "clips" ? copy.uploadClip : copy.uploadDocument}</button><MemberLink href={`/${kind}`} navigate={props.navigate}>{ui.cancel}</MemberLink></div>
    <MemberNotice notice={notice} />
  </form></section>;
}

export function MemberVerificationView(props: MemberViewProps) {
  const { ui } = props;
  const copy = memberCopy(props.locale);
  const subjects = useMemberResource("/api/verification/subjects", props.request, copy);
  const history = useMemberResource("/api/verification/requests", props.request, copy);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const uploadRef = useRef<{ file: File; id: string } | null>(null);
  useMemberPolling(history.reload, history.rows.some((row) => row.status === "pending" || row.status === "processing"));
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!file) { setNotice({ error: true, text: copy.fileRequired }); return; }
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") { setNotice({ error: true, text: copy.evidenceType }); return; }
    const form = new FormData(event.currentTarget);
    const index = Number(form.get("subject"));
    const subject = subjects.rows[index];
    if (!subject) return;
    setBusy(true);
    setNotice(null);
    try {
      let fileId = uploadRef.current?.file === file ? uploadRef.current.id : "";
      if (!fileId) {
        const uploaded = await props.upload([file], { purpose: "verification", visibility: "private" });
        fileId = String(uploaded[0]?.id || "");
        if (!fileId) throw new Error(copy.request_failed);
        uploadRef.current = { file, id: fileId };
      }
      await props.request("/api/verification/start", "POST", { subjectType: subject.subject_type, subjectId: subject.subject_id, documentType: String(form.get("documentType")), fileId, note: String(form.get("note") || "") });
      setNotice({ error: false, text: copy.verificationSent });
      history.reload();
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); }
    finally { setBusy(false); }
  }
  return <section className="contract-page member-content-page"><h1>{ui.verify}</h1>
    <MemberResourceState resource={subjects} ui={ui} empty={copy.noContent}>
      {subjects.rows.length > 0 && <form className="settings-form member-form" onSubmit={submit}>
        <label><span>{ui.subjectId}</span><select name="subject" required>{subjects.rows.map((row, index) => <option value={index} key={`${String(row.subject_type)}:${String(row.subject_id)}`}>{String(row.label)}</option>)}</select></label>
        <label><span>{ui.documentType}</span><select name="documentType" required><option value="identity">{ui.identity}</option><option value="work">{ui.workDocument}</option><option value="company">{ui.company}</option></select></label>
        <label><span>{copy.evidence}</span><input type="file" accept="image/*,application/pdf" required disabled={busy} onChange={(event) => { setFile(event.currentTarget.files?.[0] || null); uploadRef.current = null; setNotice(null); }} /><small>{copy.evidenceHelp}</small></label>
        <label><span>{ui.note}</span><textarea name="note" maxLength={2000} /></label>
        <button className="base-button primary" type="submit" disabled={busy}>{busy ? ui.submittingVerification : ui.verify}</button>
        <MemberNotice notice={notice} />
      </form>}
    </MemberResourceState>
    <section className="member-history"><h2>{copy.verificationHistory}</h2><MemberResourceState resource={history} ui={ui} empty={copy.verificationEmpty}>
      {history.rows.map((row) => <article className="member-content-card" key={String(row.id)}>
        <header><strong>{memberStatusLabel(row.status, copy, ui)}</strong><time dateTime={rowText(row, "created_at")}>{dateText(row.created_at, props.locale)}</time></header>
        <p>{row.document_type === "identity" ? ui.identity : row.document_type === "work" ? ui.workDocument : ui.company}</p>
        {Boolean(row.review_note) && <p><strong>{copy.reviewNote}: </strong>{String(row.review_note)}</p>}
      </article>)}
    </MemberResourceState></section>
  </section>;
}

export function MemberExportSettings(props: MemberViewProps) {
  const copy = memberCopy(props.locale);
  const resource = useMemberResource("/api/settings/data-export", props.request, copy);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  useMemberPolling(resource.reload, resource.rows.some((row) => row.status === "queued" || row.status === "processing" || row.status === "pending"));
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setNotice(null);
    try {
      await props.request("/api/settings/data-export", "POST", { scope: String(form.get("scope")) });
      setNotice({ error: false, text: copy.exportRequested });
      resource.reload();
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); }
    finally { setBusy(false); }
  }
  return <div className="member-settings-stack"><form className="settings-form member-form" onSubmit={submit}><p>{copy.exportHelp}</p>
    <label><span>{props.ui.exportScope}</span><select name="scope"><option value="all">{props.ui.allAccountData}</option><option value="posts">{props.ui.posts}</option><option value="media">{props.ui.media}</option></select></label>
    <button className="base-button primary" type="submit" disabled={busy}>{busy ? props.ui.submitting : copy.requestExport}</button><MemberNotice notice={notice} />
  </form><section className="member-history"><h2>{copy.exportHistory}</h2><MemberResourceState resource={resource} ui={props.ui} empty={copy.exportEmpty}>
    {resource.rows.map((row) => {
      const ready = row.status === "ready" || row.status === "completed";
      const expired = typeof row.expires_at === "string" && Date.parse(row.expires_at) <= Date.now();
      return <article className="member-content-card" key={String(row.id)}><header><strong>{expired ? copy.status_expired : memberStatusLabel(row.status, copy, props.ui)}</strong><time dateTime={rowText(row, "created_at")}>{dateText(row.created_at, props.locale)}</time></header>
        {ready && !expired && <a className="base-button outlined" href={`/api/settings/data-export/${encodeURIComponent(String(row.id))}/download`} download>{copy.downloadExport}</a>}
        {Boolean(row.expires_at) && <small>{props.ui.expiresAt}: {dateText(row.expires_at, props.locale)}</small>}
      </article>;
    })}
  </MemberResourceState></section></div>;
}

export function MemberSessionSettings(props: MemberViewProps) {
  const copy = memberCopy(props.locale);
  const resource = useMemberResource("/api/auth/sessions", props.request, copy);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  async function revoke(id?: string) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await props.request(id ? `/api/auth/sessions/${encodeURIComponent(id)}` : "/api/auth/sessions?others=1", "DELETE");
      setNotice({ error: false, text: copy.sessionsRevoked });
      resource.reload();
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); }
    finally { setBusy(false); }
  }
  return <section className="member-history"><header className="member-heading"><h2>{copy.activeSessions}</h2><button className="base-button outlined" type="button" disabled={busy || !resource.rows.some((row) => !row.current)} onClick={() => void revoke()}>{copy.revokeOtherSessions}</button></header><MemberNotice notice={notice} />
    <MemberResourceState resource={resource} ui={props.ui} empty={props.ui.noActiveSessions}>{resource.rows.map((row) => <article className="member-content-card" key={String(row.id)}>
      <header><strong>{row.current ? copy.thisSession : rowText(row, "user_agent")}</strong><time dateTime={rowText(row, "created_at")}>{dateText(row.created_at, props.locale)}</time></header>
      {Boolean(row.ip_country) && <p>{String(row.ip_country)}</p>}
      {!row.current && <button className="base-button outlined" type="button" disabled={busy} onClick={() => void revoke(String(row.id))}>{copy.revokeSession}</button>}
    </article>)}</MemberResourceState>
  </section>;
}

function MemberReauthentication({ props, purpose, optional = false }: { props: MemberViewProps & { currentUrl: string }; purpose: "email" | "delete"; optional?: boolean }) {
  const copy = memberCopy(props.locale);
  const providers = useMemberResource("/api/auth/reauthenticate/providers", props.request, copy);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  async function confirm(provider: string) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await props.request("/api/auth/openid/start", "POST", { provider, reauthenticate: true, purpose, redirect: props.currentUrl });
      if (!isRecord(result) || typeof result.authorizationUrl !== "string") throw new Error(copy.request_failed);
      window.location.assign(result.authorizationUrl);
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); setBusy(false); }
  }
  if (optional && !providers.loading && !providers.error && providers.rows.length === 0) return null;
  return <section className="member-reauthentication"><p>{copy.openIdConfirmation}</p><MemberResourceState resource={providers} ui={props.ui} empty={copy.account_confirmation_required}>
    <div className="member-actions">{providers.rows.map((row) => <button className="base-button outlined" type="button" key={String(row.provider)} disabled={busy} onClick={() => void confirm(String(row.provider))}>{String(row.label || row.provider)}</button>)}</div>
  </MemberResourceState><MemberNotice notice={notice} /></section>;
}

export function MemberEmailSettings(props: MemberViewProps & { email: string; hasPassword: boolean; currentUrl: string; confirmationToken?: string; reauthenticated?: boolean }) {
  const copy = memberCopy(props.locale);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [confirming, setConfirming] = useState(Boolean(props.confirmationToken));
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setNotice(null);
    try {
      if (confirming) {
        await props.request("/api/settings/email/confirm", "POST", { token: String(form.get("token")) });
        window.location.assign(`/settings/email?lang=${encodeURIComponent(props.locale)}`);
      } else {
        await props.request("/api/settings/email", "POST", { email: String(form.get("email")), ...(props.hasPassword && !props.reauthenticated ? { currentPassword: String(form.get("currentPassword")) } : {}) });
        setConfirming(true);
        setNotice({ error: false, text: copy.emailVerificationSent });
      }
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); }
    finally { setBusy(false); }
  }
  return <div className="member-settings-stack"><p>{props.ui.email}: {props.email}</p>
    {!props.reauthenticated && !confirming && <MemberReauthentication props={props} purpose="email" optional={props.hasPassword} />}
    <form className="settings-form member-form" onSubmit={submit}>
      {confirming ? <label><span>{props.ui.verificationCode}</span><input name="token" required autoComplete="one-time-code" defaultValue={props.confirmationToken || ""} /></label> : <>
        <label><span>{copy.newEmail}</span><input name="email" type="email" required autoComplete="email" /></label>
        {props.hasPassword && !props.reauthenticated && <label><span>{props.ui.currentPassword}</span><input name="currentPassword" type="password" autoComplete="current-password" required /></label>}
      </>}
      <button className="base-button primary" type="submit" disabled={busy}>{busy ? props.ui.submitting : confirming ? copy.confirmEmailChange : copy.changeEmail}</button><MemberNotice notice={notice} />
    </form>
  </div>;
}

export function MemberDeleteAccountSettings(props: MemberViewProps & { hasPassword: boolean; currentUrl: string; reauthenticated?: boolean }) {
  const copy = memberCopy(props.locale);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    if (String(form.get("confirm")) !== "DELETE" || !form.get("consent")) { setNotice({ error: true, text: copy.invalid_delete_confirmation }); return; }
    setBusy(true);
    setNotice(null);
    try {
      await props.request("/api/settings/account-delete", "POST", { confirm: "DELETE", ...(props.hasPassword && !props.reauthenticated ? { currentPassword: String(form.get("currentPassword")) } : {}) });
      window.location.assign(`/login?lang=${encodeURIComponent(props.locale)}`);
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); setBusy(false); }
  }
  return <section className="member-settings-stack"><h2>{copy.deleteAccount}</h2><p>{copy.deleteAccountHelp}</p>
    {!props.reauthenticated && <MemberReauthentication props={props} purpose="delete" optional={props.hasPassword} />}
    <form className="settings-form member-form" onSubmit={submit}>
      <label><span>{props.ui.typeDelete}</span><input name="confirm" required pattern="DELETE" autoComplete="off" spellCheck={false} /></label>
      {props.hasPassword && !props.reauthenticated && <label><span>{props.ui.currentPassword}</span><input name="currentPassword" type="password" required autoComplete="current-password" /></label>}
      <label className="member-consent"><input name="consent" type="checkbox" required /><span>{copy.deleteAccountConsent}</span></label>
      <button className="base-button danger" type="submit" disabled={busy}>{busy ? props.ui.submitting : copy.deleteAccount}</button><MemberNotice notice={notice} />
    </form>
  </section>;
}

export function MemberWithdrawalForm(props: MemberViewProps & { onRequested?: () => void }) {
  const copy = memberCopy(props.locale);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const keyRef = useRef<{ signature: string; key: string } | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    const payload = { amountCents: Math.round(Number(form.get("amount")) * 100), currency: "EUR", destination: { accountHolder: String(form.get("accountHolder")), iban: String(form.get("iban")) } };
    const signature = JSON.stringify(payload);
    if (keyRef.current?.signature !== signature) keyRef.current = { signature, key: crypto.randomUUID() };
    setBusy(true);
    setNotice(null);
    try {
      await props.request("/api/wallet/withdraw", "POST", payload, keyRef.current.key);
      keyRef.current = null;
      formRef.current?.reset();
      setNotice({ error: false, text: copy.withdrawalRequested });
      props.onRequested?.();
    } catch (error) { setNotice({ error: true, text: memberError(error, copy) }); }
    finally { setBusy(false); }
  }
  return <section className="member-settings-stack"><h2>{copy.requestWithdrawal}</h2><p>{copy.withdrawalHelp}</p><form className="settings-form member-form" ref={formRef} onSubmit={submit}>
    <label><span>{props.ui.amountEur}</span><input name="amount" type="number" min="0.01" step="0.01" required inputMode="decimal" /></label>
    <label><span>{copy.beneficiary}</span><input name="accountHolder" required minLength={2} maxLength={160} autoComplete="name" /></label>
    <label><span>{copy.iban}</span><input name="iban" required minLength={15} maxLength={42} autoCapitalize="characters" spellCheck={false} /></label>
    <div className="form-actions"><button className="base-button primary" type="submit" disabled={busy}>{busy ? props.ui.submitting : copy.requestWithdrawal}</button><MemberLink href="/wallet/transactions" navigate={props.navigate}>{copy.transactions}</MemberLink></div><MemberNotice notice={notice} />
  </form></section>;
}

export function MemberTransactionList(props: MemberViewProps) {
  const copy = memberCopy(props.locale);
  const resource = useMemberResource("/api/wallet", props.request, copy);
  useMemberPolling(resource.reload, resource.rows.some((row) => row.status === "pending" || row.status === "processing"));
  return <section className="member-history"><h2>{copy.transactions}</h2><MemberResourceState resource={resource} ui={props.ui} empty={props.ui.walletEmpty}>
    {resource.rows.map((row) => {
      const key = `transaction_${String(row.type)}`;
      const title = Object.hasOwn(copy, key) ? copy[key as keyof MemberCopy] : copy.transactions;
      return <article className="member-content-card" key={String(row.id)}><header><strong>{title}</strong><strong>{money(row.amount_cents, row.currency, props.locale)}</strong></header>
        <div className="member-meta"><span>{memberStatusLabel(row.status, copy, props.ui)}</span><time dateTime={rowText(row, "created_at")}>{dateText(row.created_at, props.locale)}</time></div>
        {Boolean(row.review_note) && <p>{String(row.review_note)}</p>}
        {Boolean(row.reference) && <p>{copy.transferReference}: {String(row.reference)}</p>}
        {Boolean(row.paid_at) && <p>{copy.paidAt}: {dateText(row.paid_at, props.locale)}</p>}
      </article>;
    })}
  </MemberResourceState></section>;
}

export function MemberPaymentStatus(props: MemberViewProps & { paymentId: string; onCompleted?: () => void }) {
  const copy = memberCopy(props.locale);
  const resource = useMemberResource(`/api/payments/checkouts/${encodeURIComponent(props.paymentId)}`, props.request, copy);
  const row = resource.rows[0];
  const status = rowText(row || {}, "status");
  const terminal = ["completed", "failed", "expired", "cancelled"].includes(status);
  const completedRef = useRef("");
  useEffect(() => {
    if (status === "completed" && completedRef.current !== props.paymentId) {
      completedRef.current = props.paymentId;
      props.onCompleted?.();
    }
  }, [status, props.paymentId, props.onCompleted]);
  useMemberPolling(resource.reload, Boolean(row) && !terminal);
  return <section className="member-history"><h2>{copy.paymentStatus}</h2><MemberResourceState resource={resource} ui={props.ui} empty={copy.content_not_found}>
    {row && <article className="member-content-card"><header><strong>{memberStatusLabel(status, copy, props.ui)}</strong><strong>{money(row.amount_cents, row.currency, props.locale)}</strong></header>
      <p role="status">{status === "completed" ? copy.paymentCompleted : terminal ? copy.payment_checkout_closed : copy.paymentPending}</p>
      <time dateTime={rowText(row, "created_at")}>{dateText(row.created_at, props.locale)}</time>
      <MemberLink href="/wallet/transactions" navigate={props.navigate}>{copy.transactions}</MemberLink>
    </article>}
  </MemberResourceState></section>;
}
